import { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button, Checkbox, Input, InputNumber, Modal, Table, Tag, message } from 'antd'
import { useSelector } from 'react-redux'
import Select from '@UI/New/Select'
import { selectProfile } from '@store/profile.reducer'
import { getBaseRole } from '@/helpers/customRoles'
import { studentBillingApi as api } from '@/api/queries/studentBilling'
import s from './index.module.css'

const units = [
  { value: 'months', label: 'Месяцы' }, { value: 'weeks', label: 'Недели' },
  { value: 'days', label: 'Дни' }, { value: 'dates', label: 'Точные даты' },
]
const errorMessage = (error) => message.error(error?.response?.data?.error || error.message || 'Не удалось выполнить операцию')
const money = (value) => Number(value).toLocaleString('ru-RU', { style: 'currency', currency: 'RUB' })

const Choice = ({ value, onChange, options, placeholder, ...props }) => (
  <Select fields={{ value, onChange, options, placeholder, style: { minWidth: 190, width: '100%' }, ...props }} />
)

const InvoiceForm = ({ userId, companies, onCreated, onCancel }) => {
  const [students, setStudents] = useState([])
  const [products, setProducts] = useState([])
  const [student, setStudent] = useState(userId)
  const [productKey, setProductKey] = useState()
  const [company, setCompany] = useState(0)
  const [unit, setUnit] = useState('months')
  const [value, setValue] = useState(1)
  const [from, setFrom] = useState('')
  const [until, setUntil] = useState('')
  const [amount, setAmount] = useState(0)
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)
  const key = useRef(`manual:${Date.now()}:${Math.random().toString(36).slice(2)}`)
  const searchSequence = useRef(0)
  const findStudents = useCallback(async (search) => {
    const sequence = ++searchSequence.current
    try {
      const result = await api.students(search)
      if (sequence === searchSequence.current) setStudents(result)
    } catch (error) { errorMessage(error) }
  }, [])
  useEffect(() => { if (!userId) findStudents('') }, [userId, findStudents])
  useEffect(() => {
    let active = true
    setProducts([])
    setProductKey(undefined)
    if (student) api.products(student).then((result) => { if (active) setProducts(result) }).catch(errorMessage)
    return () => { active = false }
  }, [student])
  const product = products.find((item) => `${item.target_type}:${item.target_id}` === productKey)
  const selectProduct = (next) => {
    const item = products.find((p) => `${p.target_type}:${p.target_id}` === next)
    setProductKey(next)
    setUnit(item.by_lessons ? 'lessons' : 'months')
    setValue(1)
    setAmount(item.suggested_amount)
  }
  const submit = async () => {
    setBusy(true)
    try {
      await api.create({ user_id: student, target_type: product.target_type, target_id: product.target_id,
        company_id: company, period_unit: unit, period_value: value, period_from: from, period_until: until,
        amount, description, request_key: key.current })
      onCreated()
    } catch (error) { errorMessage(error) } finally { setBusy(false) }
  }
  return (
    <Modal open title="Выставить счёт" onCancel={onCancel} onOk={submit} confirmLoading={busy}
      okText="Создать счёт" cancelText="Отмена" okButtonProps={{ disabled: !student || !product || !amount || !companies.length }}>
      <div className={s.form}>
        {!userId && <label>Ученик<Choice value={student} onChange={setStudent} placeholder="Найти ученика" showSearch filterOption={false}
          onSearch={findStudents} options={students.map((item) => ({ value: Number(item.id), label: item.name }))} /></label>}
        <label>Курс или класс<Choice value={productKey} onChange={selectProduct} placeholder="Выберите продукт"
          options={products.map((item) => ({ value: `${item.target_type}:${item.target_id}`, label: item.label }))} /></label>
        <label>Компания<Choice value={company} onChange={setCompany} options={companies.map((item) => ({ value: item.id, label: `${item.company_name || 'Основная компания'}${item.is_primary ? ' (основная)' : ''}` }))} /></label>
        <label>Период оплаты<Choice value={unit} onChange={setUnit} options={product?.by_lessons ? [{ value: 'lessons', label: 'Занятия' }] : units} /></label>
        {unit === 'dates' ? <div className={s.filters}>
          <label>С<Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
          <label>По<Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} /></label>
        </div> : <label>Количество<InputNumber min={1} max={10000} precision={0} value={value} onChange={setValue} /></label>}
        <label>Сумма, ₽<InputNumber min={0.01} max={9999999999.99} precision={2} value={amount} onChange={setAmount} /></label>
        <label>Наименование услуги<Input.TextArea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="По умолчанию: продукт и период оплаты" /></label>
        {!companies.some((item) => item.id === company && item.is_complete && item.vat_type) && <Alert type="warning" message="Заполните реквизиты и НДС выбранной компании в настройках → О компании." />}
      </div>
    </Modal>
  )
}

const PaymentModal = ({ row, onDone, onCancel }) => {
  const paid = row.status !== 'paid'
  const [preview, setPreview] = useState(null)
  const [previewError, setPreviewError] = useState('')
  const [skip, setSkip] = useState(false)
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    let active = true
    api.preview(row.id, paid).then((result) => { if (active) setPreview(result) })
      .catch((error) => { if (active) setPreviewError(error?.response?.data?.error || error.message) })
    return () => { active = false }
  }, [row.id, paid])
  const submit = async () => {
    setBusy(true)
    try { await api.setPaid(row.id, paid, skip); onDone() } catch (error) { errorMessage(error) } finally { setBusy(false) }
  }
  return <Modal open title={paid ? 'Подтвердить оплату' : 'Отменить оплату'} onCancel={onCancel} onOk={submit}
    confirmLoading={busy} okText="Подтвердить" cancelText="Отмена" okButtonProps={{ disabled: !skip && !preview }}>
    <p>Счёт № {row.id} · {money(row.amount)}</p>
    <Alert type={previewError ? 'warning' : 'info'} message={previewError || preview?.message || 'Расчёт изменения доступа…'} />
    <p><Checkbox checked={skip} onChange={(e) => setSkip(e.target.checked)}>
      {paid ? 'Только отметить оплату, без продления доступа и пополнения баланса' : 'Только отменить статус оплаты, без изменения доступа и баланса'}
    </Checkbox></p>
  </Modal>
}

const StudentBilling = ({ userId }) => {
  const profile = useSelector(selectProfile)
  const manager = ['admin', 'junior-admin'].includes(getBaseRole(profile?.role))
  const [data, setData] = useState({ items: [], total: 0 })
  const [filters, setFilters] = useState({})
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [companies, setCompanies] = useState([])
  const [creating, setCreating] = useState(false)
  const [payment, setPayment] = useState(null)
  const [revision, setRevision] = useState(0)
  const [actionBusy, setActionBusy] = useState(false)
  const refresh = () => setRevision((n) => n + 1)
  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    api.list({ ...filters, ...(userId ? { user_id: userId } : {}), page }).then((result) => { if (active) setData(result) })
      .catch((e) => { if (active) setError(e?.response?.data?.error || e.message) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [userId, filters, page, revision])
  useEffect(() => {
    if (manager) api.companies().then(setCompanies).catch(errorMessage)
  }, [manager])
  const filter = (name, value) => { setPage(1); setFilters((current) => ({ ...current, [name]: value })) }
  const download = async (row, type) => {
    try {
      const response = await api.download(row.id, type)
      const url = URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = url
      link.download = `${type === 'act' ? 'УПД' : 'Счёт'}-${row.id}.pdf`
      link.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (e) { errorMessage(e) }
  }
  const mutate = async (operation) => {
    setActionBusy(true)
    try { await operation(); refresh() } catch (e) { errorMessage(e) } finally { setActionBusy(false) }
  }
  const columns = [
    { title: 'Дата', dataIndex: 'created_at', render: (value, row) => <span>№ {row.id}<br />{value?.slice(0, 10)}</span> },
    ...(!userId ? [{ title: 'Ученик', render: (_, row) => <span>{row.product.student_name}<br /><small>ID {row.user_id}</small></span> }] : []),
    { title: 'Продукт', render: (_, row) => <span>{row.product.label}<br /><small>{row.description}</small></span> },
    { title: 'Период', dataIndex: 'period_label' },
    { title: 'Сумма', dataIndex: 'amount', render: money },
    { title: 'Создан', dataIndex: 'source', render: (value) => value === 'notification' ? 'Уведомление' : 'Вручную' },
    { title: 'Счёт', render: (_, row) => <div className={s.actions}>
      <Button size="small" onClick={() => download(row, 'invoice')}>PDF</Button>
      <Tag color={row.status === 'paid' ? 'green' : 'default'}>{row.status === 'paid' ? 'Оплачен' : 'Не оплачен'}</Tag>
    </div> },
    { title: 'Акт / УПД', render: (_, row) => row.act_created_at
      ? <Button size="small" onClick={() => download(row, 'act')}>PDF</Button>
      : manager && <Button size="small" disabled={actionBusy} onClick={() => Modal.confirm({ title: 'Создать УПД на полную сумму счёта?', content: `${row.product.label} · ${money(row.amount)}`, okText: 'Создать', cancelText: 'Отмена', onOk: () => mutate(() => api.createAct(row.id)) })}>+ Создать</Button> },
    ...(manager ? [{ title: 'Действия', render: (_, row) => <div className={s.actions}>
      <Button size="small" onClick={() => setPayment(row)}>{row.status === 'paid' ? 'Отменить оплату' : 'Отметить оплату'}</Button>
      <span title={row.status === 'paid' ? 'Сначала отмените оплату' : ''}><Button size="small" danger disabled={actionBusy || row.status === 'paid'}
        onClick={() => Modal.confirm({ title: `Удалить неоплаченный счёт № ${row.id}?`, okText: 'Удалить', cancelText: 'Отмена', onOk: () => mutate(() => api.remove(row.id)) })}>Удалить</Button></span>
    </div> }] : []),
  ]
  return <section>
    <div className={s.toolbar}><h2>Счета и акты</h2>{manager && <Button type="primary" onClick={() => setCreating(true)}>Выставить счёт</Button>}</div>
    <div className={s.filters}>
      <label>С<Input type="date" value={filters.from || ''} onChange={(e) => filter('from', e.target.value)} /></label>
      <label>По<Input type="date" value={filters.until || ''} onChange={(e) => filter('until', e.target.value)} /></label>
      <label>Статус<Choice value={filters.status} onChange={(value) => filter('status', value)} allowClear placeholder="Все статусы" options={[{ value: 'issued', label: 'Не оплачен' }, { value: 'paid', label: 'Оплачен' }]} /></label>
      <label>Акт<Choice value={filters.has_act} onChange={(value) => filter('has_act', value)} allowClear placeholder="Все счета" options={[{ value: '1', label: 'С актом' }, { value: '0', label: 'Без акта' }]} /></label>
      <label>Создан<Choice value={filters.source} onChange={(value) => filter('source', value)} allowClear placeholder="Все источники" options={[{ value: 'manual', label: 'Вручную' }, { value: 'notification', label: 'Уведомление' }]} /></label>
      <label>Продукт / ученик<Input.Search placeholder="Название или имя" allowClear onSearch={(value) => filter('search', value)} /></label>
      {manager && <label>Компания<Choice value={filters.company_id} onChange={(value) => filter('company_id', value)} allowClear placeholder="Все компании" options={companies.map((item) => ({ value: item.id, label: item.company_name || 'Основная компания' }))} /></label>}
    </div>
    {error && <Alert type="error" message={error} />}
    <Table rowKey="id" columns={columns} dataSource={data.items} loading={loading} scroll={{ x: 1100 }}
      locale={{ emptyText: 'Счетов пока нет' }} pagination={{ current: page, pageSize: 30, total: data.total, showSizeChanger: false, onChange: setPage }} />
    {creating && <InvoiceForm userId={userId} companies={companies} onCancel={() => setCreating(false)} onCreated={() => { setCreating(false); setPage(1); refresh() }} />}
    {payment && <PaymentModal row={payment} onCancel={() => setPayment(null)} onDone={() => { setPayment(null); refresh() }} />}
  </section>
}

export const StudentBillingButton = ({ userId }) => {
  const profile = useSelector(selectProfile)
  const role = getBaseRole(profile?.role)
  const manager = ['admin', 'junior-admin'].includes(role)
  const allowed = manager || ['children', 'parent'].includes(role)
  const [count, setCount] = useState(0)
  const [open, setOpen] = useState(false)
  useEffect(() => {
    let active = true
    setCount(0)
    setOpen(false)
    if (allowed && !manager && userId) api.list({ user_id: userId, page: 1 }).then((result) => { if (active) setCount(result.total) }).catch(() => {})
    return () => { active = false }
  }, [allowed, manager, userId])
  if (!userId || !allowed || (!manager && !count)) return null
  return <><Button onClick={() => setOpen(true)}>Счета и акты</Button>
    <Modal open={open} onCancel={() => setOpen(false)} footer={null} width={1300} destroyOnClose>
      {open && <StudentBilling userId={userId} />}
    </Modal></>
}

export default StudentBilling
